import {UserPlayer} from "@game/player/userPlayer";
import {Player} from "@game/player/player";
import {CSSProperties, useEffect, useRef, useState} from "react";
import {Tile} from "@game/map/tile";
import {GameEvent} from "@game/gameEvent";
import {Game} from "@game/game";
import {Vector2} from "@utility/vector2";
import {Bounds2} from "@utility/bounds2";
import {CompassEntry, LETTER_INDICATOR_R} from "@components/map/CompassEntry";
import {COLOURS, SCREEN_SIZE} from "@components/core/theme";

const COMPASS_SIZE = 160;
const CX = 80;
const CY = 80;
const FACE_R = 68;
const ARM_R = 60;
const MINOR_R = 24;   // 34 / √2, rounded — intercardinal tip offset along each axis
const INNER_RING_R = 56;

const styles: Record<string, CSSProperties> = {
    compass: {
        position: "absolute",
        top: 12,
        left: 12,
        width: COMPASS_SIZE,
        height: COMPASS_SIZE,
        cursor: "pointer",
        pointerEvents: "auto",
        userSelect: "none",
        ...(SCREEN_SIZE === "small" ? {transform: "scale(0.7)", transformOrigin: "top left"} : {})
    },
    letterChip: {
        width: 16,
        height: 16,
        borderRadius: 2,
        border: `1px solid ${COLOURS.darkWood}`,
        backgroundColor: COLOURS.parchment,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontFamily: "monospace",
        color: COLOURS.ink,
        boxShadow: "0 1px 3px rgba(0,0,0,0.5)"
    },
};

// Cardinal tick marks — 8 short radial lines on the inner ring
const TICKS = Array.from({length: 8}, (_, i) => {
    const rad = (i * 45) * Math.PI / 180;
    return {
        x1: CX + Math.cos(rad) * INNER_RING_R,
        y1: CY + Math.sin(rad) * INNER_RING_R,
        x2: CX + Math.cos(rad) * (INNER_RING_R + 5),
        y2: CY + Math.sin(rad) * (INNER_RING_R + 5)
    }
});

function CompassRose({needleRotation, hasTarget}: {needleRotation: number, hasTarget: boolean}) {
    const o = MINOR_R;
    const counterR = Math.round(ARM_R * 0.45);
    const needleRed = hasTarget ? "#cc2200" : COLOURS.agedBorder;
    const needleDark = hasTarget ? COLOURS.darkWood : COLOURS.agedBorder;
    return (
        <svg width={COMPASS_SIZE} height={COMPASS_SIZE} style={{position: "absolute", top: 0, left: 0}}>
            <style>{`
                @keyframes needle-wobble {
                    0%   { transform: rotate(-1deg); }
                    30%  { transform: rotate(1.5deg); }
                    65%  { transform: rotate(-1.5deg); }
                    85%  { transform: rotate(0.8deg); }
                    100% { transform: rotate(-1deg); }
                }
            `}</style>
            {/* Drop shadow */}
            <circle cx={CX} cy={CY} r={FACE_R + 3} fill="rgba(0,0,0,0.22)"/>
            {/* Face */}
            <circle cx={CX} cy={CY} r={FACE_R} fill={COLOURS.parchment} stroke={COLOURS.darkWood} strokeWidth="3"/>
            {/* Inner decoration ring */}
            <circle cx={CX} cy={CY} r={INNER_RING_R} fill="none" stroke={COLOURS.agedBorder} strokeWidth="0.75"/>
            {/* Tick marks */}
            {TICKS.map((t, i) => (
                <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
                      stroke={COLOURS.agedBorder} strokeWidth="1"/>
            ))}
            {/* Intercardinal minor arms — drawn before major so major sits on top */}
            <polygon points={`${CX+o},${CY-o} ${CX+3},${CY+3} ${CX-3},${CY-3}`} fill={COLOURS.agedBorder}/>
            <polygon points={`${CX-o},${CY-o} ${CX+3},${CY-3} ${CX-3},${CY+3}`} fill={COLOURS.agedBorder}/>
            <polygon points={`${CX+o},${CY+o} ${CX-3},${CY+3} ${CX+3},${CY-3}`} fill={COLOURS.agedBorder}/>
            <polygon points={`${CX-o},${CY+o} ${CX-3},${CY-3} ${CX+3},${CY+3}`} fill={COLOURS.agedBorder}/>
            {/* Static cardinal arms — N, S, E, W */}
            <polygon points={`${CX},${CY-ARM_R} ${CX+6},${CY} ${CX-6},${CY}`}   fill={COLOURS.amber}/>
            <polygon points={`${CX},${CY+ARM_R} ${CX+6},${CY} ${CX-6},${CY}`}   fill={COLOURS.amber}/>
            <polygon points={`${CX+ARM_R},${CY} ${CX},${CY-6} ${CX},${CY+6}`}   fill={COLOURS.amber}/>
            <polygon points={`${CX-ARM_R},${CY} ${CX},${CY-6} ${CX},${CY+6}`}   fill={COLOURS.amber}/>
            {/* Needle — outer g: smooth CSS rotation; inner g: wobble only when tracking */}
            <g style={{
                transformBox: "view-box",
                transformOrigin: `${CX}px ${CY}px`,
                transform: `rotate(${needleRotation}deg)`,
                transition: "transform 0.5s ease-out"
            }}>
                <g style={{
                    transformBox: "view-box",
                    transformOrigin: `${CX}px ${CY}px`,
                    animation: hasTarget ? "needle-wobble 3.5s ease-in-out infinite" : "none"
                }}>
                    <polygon points={`${CX},${CY-ARM_R} ${CX+4},${CY} ${CX-4},${CY}`}    fill={needleRed}/>
                    <polygon points={`${CX},${CY+counterR} ${CX+4},${CY} ${CX-4},${CY}`} fill={needleDark}/>
                </g>
            </g>
            {/* Centre pin — drawn last to sit on top of needle */}
            <circle cx={CX} cy={CY} r={4} fill={COLOURS.darkWood}/>
            <circle cx={CX} cy={CY} r={2} fill={COLOURS.amber}/>
            {/* Cardinal labels */}
            <text x={CX}      y={CY - 46} textAnchor="middle" dominantBaseline="middle"
                  fontFamily="Georgia, serif" fontSize="12" fontWeight="bold" fill="#cc2200">N</text>
            <text x={CX}      y={CY + 46} textAnchor="middle" dominantBaseline="middle"
                  fontFamily="Georgia, serif" fontSize="10" fill={COLOURS.ink}>S</text>
            <text x={CX + 46} y={CY}      textAnchor="middle" dominantBaseline="middle"
                  fontFamily="Georgia, serif" fontSize="10" fill={COLOURS.ink}>E</text>
            <text x={CX - 46} y={CY}      textAnchor="middle" dominantBaseline="middle"
                  fontFamily="Georgia, serif" fontSize="10" fill={COLOURS.ink}>W</text>
        </svg>
    );
}

const TILE_FADE_MS = 300;
const tileKey = (t: Tile) => `${t.position.x},${t.position.y}`;
type AnimatedEntry = {key: string, tile: Tile, opacity: number};

interface ICompass {
    game: Game
    onHome: () => void
    userPlayer: UserPlayer
    viewportCenter: Vector2
    viewportTileBounds: Bounds2
}
export function Compass({game, onHome, userPlayer, viewportCenter, viewportTileBounds}: ICompass) {
    const [allNearbyPlayers, setAllNearbyPlayers] = useState<Player[]>([]);
    const [nearestLetterTiles, setNearestLetterTiles] = useState<Tile[]>([]);
    const [displayRotation, setDisplayRotation] = useState(0);
    const [animatedTiles, setAnimatedTiles] = useState<AnimatedEntry[]>([]);
    const animatedTilesRef = useRef<AnimatedEntry[]>([]);
    const animTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const viewportTileBoundsRef = useRef(viewportTileBounds);
    viewportTileBoundsRef.current = viewportTileBounds;

    useEffect(() => {
        const update = (event?: GameEvent) => {
            const type = event?.type;
            if (!type || type === "Updated" || type === "Initialised" || type === "NearbyPlayersUpdated") {
                setAllNearbyPlayers(userPlayer.getNearestPlayerTiles(10));
            }
            const bounds = viewportTileBoundsRef.current;
            const nearest = userPlayer.getNearestTiles(7,
                tile => tile.letter !== undefined && !bounds.contains(tile.position));
            setNearestLetterTiles(nearest);
        };
        update();
        userPlayer.events.delegates.add(update);
        return () => userPlayer.events.delegates.remove(update);
    }, [userPlayer]);

    // Keep ref in sync so the diff effect can read current state without adding animatedTiles to deps
    useEffect(() => { animatedTilesRef.current = animatedTiles; }, [animatedTiles]);

    // Diff nearestLetterTiles into animatedTiles: translate persisting, fade in arrivals, fade out departures
    useEffect(() => {
        const incomingKeys = new Set(nearestLetterTiles.map(tileKey));
        const prev = animatedTilesRef.current;
        const prevKeys = new Set(prev.map(e => e.key));

        // Cancel any pending removal timers for tiles that just came back
        nearestLetterTiles.forEach(tile => {
            const k = tileKey(tile);
            if (animTimers.current.has(k)) {
                clearTimeout(animTimers.current.get(k)!);
                animTimers.current.delete(k);
            }
        });

        // Schedule DOM removal of tiles that are leaving (after fade-out completes)
        prev.filter(e => !incomingKeys.has(e.key)).forEach(e => {
            if (!animTimers.current.has(e.key)) {
                const t = setTimeout(() => {
                    setAnimatedTiles(cur => cur.filter(x => x.key !== e.key));
                    animTimers.current.delete(e.key);
                }, TILE_FADE_MS + 50);
                animTimers.current.set(e.key, t);
            }
        });

        const newTiles = nearestLetterTiles.filter(t => !prevKeys.has(tileKey(t)));

        // Commit: update persistent (opacity 1), mark departing (opacity 0), add arrivals (opacity 0)
        setAnimatedTiles([
            ...prev.map(e => ({
                ...e,
                tile: incomingKeys.has(e.key) ? nearestLetterTiles.find(t => tileKey(t) === e.key)! : e.tile,
                opacity: incomingKeys.has(e.key) ? 1 : 0,
            })),
            ...newTiles.map(t => ({key: tileKey(t), tile: t, opacity: 0})),
        ]);

        // RAF: flip new arrivals from opacity 0 → 1 after they're in the DOM
        if (newTiles.length === 0) return;
        const newKeys = new Set(newTiles.map(tileKey));
        const rafId = requestAnimationFrame(() => {
            setAnimatedTiles(cur => cur.map(e => newKeys.has(e.key) ? {...e, opacity: 1} : e));
        });
        return () => cancelAnimationFrame(rafId);
    }, [nearestLetterTiles]);

    // Cleanup all pending timers on unmount
    useEffect(() => () => { animTimers.current.forEach(clearTimeout); }, []);

    // Sort players by distance from the current viewport centre (tile space)
    const sorted = allNearbyPlayers
        .map(p => ({ p, pos: p.getPosition(game.size) }))
        .sort((a, b) => {
            const da = (a.pos.x - viewportCenter.x) ** 2 + (a.pos.y - viewportCenter.y) ** 2;
            const db = (b.pos.x - viewportCenter.x) ** 2 + (b.pos.y - viewportCenter.y) ** 2;
            return da - db;
        });

    const getAngle = (pos: {x: number, y: number}) =>
        Math.atan2(pos.y - viewportCenter.y, pos.x - viewportCenter.x);

    const nearest = sorted[0];
    const nearestPlayerPos = nearest?.pos ?? null;
    const targetRotation = nearestPlayerPos
        ? (getAngle(nearestPlayerPos) * 180 / Math.PI) + 90
        : 0; // North

    // Normalise delta to [-180, 180] so the needle always takes the short arc
    useEffect(() => {
        setDisplayRotation(prev => {
            let delta = targetRotation - prev;
            while (delta > 180) delta -= 360;
            while (delta < -180) delta += 360;
            return prev + delta;
        });
    }, [targetRotation]);

    return (
        <div onClick={onHome} style={styles.compass}>
            <CompassRose needleRotation={displayRotation} hasTarget={!!nearest}/>
            {animatedTiles.map(entry => (
                <CompassEntry key={entry.key} angle={getAngle(entry.tile.position)}
                              opacity={entry.opacity} radius={LETTER_INDICATOR_R}>
                    <span style={styles.letterChip}>{entry.tile.letter?.letter}</span>
                </CompassEntry>
            ))}
        </div>
    );
}
